import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import {BaseSeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import HelpButton from 'help/HelpButton';
import {
  StudySingleProcedureRunAnalysisQuery,
  StudySingleProcedureRunAnalysisQueryVariables,
} from 'studies/typed/shared/__generated/StudySingleProcedureRunAnalysisQuery';
import OOTBChartingCarousel from 'studies/typed/shared/OOTBChartingCarousel';
import {
  INACCESSIBLE_STUDY_ERROR_TEXT,
  SELECTED_UNIT_OPERATIONS_LABEL,
} from 'studies/typed/shared/ProcedureRunAnalysisConstants';
import {StudyInaccessibleContentBanner} from 'studies/typed/utils/ui/StudyQueryBanners';
import {miniSpacingLeft} from 'styles/Utilities';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export const INACCESSIBLE_RUN_ERROR_TEXT =
  'This run is no longer accessible. Try refreshing the page if this is unexpected.';
export const OOTB_RUN_DESCRIPTION = "Preview what we'll build to help you learn about your run.";

export type SingleProcedureRunSeedDataConfig = BaseSeedDataConfig<
  SeedDataConfigType.PROCEDURE_RUN,
  {procedureRunId: string}
>;

export function validateSingleProcedureRunSeedDataConfigValid(
  config: SingleProcedureRunSeedDataConfig
): boolean {
  // None of the fields in SingleProcedureRunSeedDataConfig are editable, so the config is always valid.
  return true;
}

export function SingleProcedureRunSeedDataConfigEditor(props: {
  value: SingleProcedureRunSeedDataConfig;
  onChange: (value: SingleProcedureRunSeedDataConfig) => void;
}): JSX.Element {
  const result = useBenchlingQuery<
    StudySingleProcedureRunAnalysisQuery,
    StudySingleProcedureRunAnalysisQueryVariables
  >(PMEI_QUERY, {variables: {id: props.value.data.procedureRunId}, renderCachedDataWhenRefetching: true});
  return (
    <FormGroup>
      <FieldLabel label={SELECTED_UNIT_OPERATIONS_LABEL} />
      <HelpButton
        renderContent={() => (
          <p className={css(styles.helpToolTipText)}>
            Total instances is taken from the number of unit operations, conditions, and replicates.
          </p>
        )}
        iconClassName={css(styles.helpToolTip)}
        interaction={FloatingInteraction.HOVER}
      />
      {(() => {
        if (result.type === QueryResultType.PENDING || result.type === QueryResultType.ERROR) {
          return <CenteredHorizontalThrobber />;
        }
        if (isInaccessible(result.data.procedureRun)) {
          return <InlineBanner bannerType={InlineBannerType.ERROR} text={INACCESSIBLE_RUN_ERROR_TEXT} />;
        }
        const study = result.data.procedureRun.study;
        if (isInaccessible(study)) {
          return (
            <StudyInaccessibleContentBanner
              inaccessibleInfo={study}
              modelType={ModelTypeConstants.STUDY}
              displayErrorText={INACCESSIBLE_STUDY_ERROR_TEXT}
            />
          );
        }
        return (
          <>
            <Row className={css(styles.instancesText)}>
              {result.data.procedureRun.methodExecutionInstances.length} instances
            </Row>
            {/* TODO(BNCH-136861): Promote this check to the TypedStudyAdapter */}
            {study.studySchema.studyType === StudyType.PROCESS_DEVELOPMENT && (
              <div className={css(styles.ootbCarouselContainer)}>
                <OOTBChartingCarousel description={OOTB_RUN_DESCRIPTION} />
              </div>
            )}
          </>
        );
      })()}
    </FormGroup>
  );
}

export const PMEI_QUERY: DocumentNode = gql`
  query StudySingleProcedureRunAnalysisQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $id) {
      ... on ProcedureRunNode {
        id
        study {
          ... on StudyNode {
            id
            studySchema {
              id
              studyType
            }
          }
          ...InaccessibleInfoGraphQL
        }
        methodExecutionInstances {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const styles = StyleSheet.create({
  helpToolTip: miniSpacingLeft,
  helpToolTipText: {marginTop: '5px', marginLeft: '25px'},
  instancesText: {marginLeft: '3px'},
  ootbCarouselContainer: {
    // Stretch the carousel background to the edges of the modal border.
    // * 15px between unit-op count and the carousel. This matches the margin-bottom of FormGroup.
    // * Stretch flush against the sides of the modal body: -16px to offset the padding of the modal body.
    // * Stretch flush against the bottom of the modal body:
    //   (-15px to stretch outside the FormGroup in SeedDataRender)
    //   + (-16px to offset the padding of the modal body)
    margin: '15px -16px -31px -16px',
  },
});
